<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { urlsBuilderContextToken } from "@/context_tokens";
    import { modalManagerToken } from "@/tokens";
    import checkIsMobile from "@/utils/checkIsMobile";
    import ProductEmptyIcon from "@apps/gift_registry/components/icons/ProductEmptyIcon.svelte";
    import { REGISTRY_FORM_MODAL_ACTION_TYPES } from "@apps/gift_registry/constants";
    import { RegistryFormModalWidgetDefinition } from "@apps/gift_registry/definitions";
    import { GiftRegistryWithItemsEntity } from "@apps/gift_registry/entities/GiftRegistryWithItemsEntity";
    import GiftRegistryQuery from "@apps/gift_registry/queries/GiftRegistryQuery";
    import GiftCartService from "@apps/gift_registry/services/GiftCartService";
    import GiftRegistryService from "@apps/gift_registry/services/GiftRegistryService";
    import GiftRegistryStorefrontValuesService from "@apps/gift_registry/services/GiftRegistryStorefrontValuesService";
    import { registryFormModalToken } from "@apps/gift_registry/tokens";
    import WishlistSocialShare from "@apps/wishlist/components/WishlistSocialShare/WishlistSocialShare.svelte";
    import { WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES } from "@apps/wishlist/constants/constants";
    import { filterNilValue } from "@datorama/akita";
    import ClipboardInterface from "@lib/DOMClipboardAdapter/ClipboardInterface";
    import DOMClipboardAdapter from "@lib/DOMClipboardAdapter/DOMClipboardAdapter";
    import PopupManager from "@lib/PopupManager";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { getHumanReadableDate } from "@lib/utils";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import CN from "classnames";
    import { lastValueFrom, map } from "rxjs";
    import { createEventDispatcher } from "svelte";
    import { container } from "tsyringe";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import DrawerManager from "@components/Drawer/DrawerManager";
    import EmptyState from "@components/EmptyState/EmptyState.svelte";
    import Icon from "@components/Icon.svelte";
    import ChevronIcon from "@components/icons/ChevronIcon.svelte";
    import DeleteIcon from "@components/icons/DeleteIcon.svelte";
    import EditIcon from "@components/icons/EditIcon.svelte";
    import T from "@components/T.svelte";

    import GiftRegistryPageItem from "./GiftRegistryPageItem.svelte";

    export let registry: GiftRegistryWithItemsEntity;
    export let registries: GiftRegistryWithItemsEntity[];
    export let productsQuery: ProductsQuery;
    export let giftRegistryService: GiftRegistryService;
    export let giftRegistryQuery: GiftRegistryQuery;
    export let giftRegistryStorefrontValuesService: GiftRegistryStorefrontValuesService;
    export let giftCartService: GiftCartService | null = null;
    export let drawerManager: DrawerManager | null = null;
    export let dynamicImportManager: DynamicImportManager;
    export let isSharedList = false;
    export let namespace: string;

    const sharingStatus$ = giftRegistryQuery.sharingStatus$;
    const settings$ =
        giftRegistryStorefrontValuesService.selectGiftRegistrySettings();

    const enabledSocialShareChannels$ = settings$.pipe(
        filterNilValue(),
        map((settings) => {
            return settings?.shareChannelTypes.filter(
                (channel) => channel.isEnabled
            );
        })
    );

    let isListOpened = true;
    let isShareLinkCopied = false;
    let showConfirmDelete = false;

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);
    const clipboard: ClipboardInterface = new DOMClipboardAdapter();
    const isMobile = checkIsMobile();

    const modalManager = container.resolve(modalManagerToken);
    const dispatch = createEventDispatcher();

    const i18nModule = container.resolve(I18nModule);
    const social_share_label$ = i18nModule.exports.i18n.selectT(
        namespace,
        "social_share_label"
    );
    const link_copied_label$ = i18nModule.exports.i18n.selectT(
        namespace,
        "link_copied_label"
    );

    const empty_products_label$ = i18nModule.exports.i18n.selectT(
        namespace,
        "empty_products_label"
    );

    const handleToggleListOpen = () => {
        if (!isSharedList) {
            isListOpened = !isListOpened;
        }
    };

    const handleToggleConfirmDelete = () => {
        showConfirmDelete = !showConfirmDelete;
    };

    const handleShareRegistry = async (e: CustomEvent) => {
        const channelName = e.detail;

        if (channelName === WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES.copyShareLink) {
            handleCopyShareLink(channelName, registry.id);
        } else {
            handleOpenPopup(channelName, registry.id);
        }
    };

    const handleDeleteRegistry = () => {
        dispatch("deleteRegistry", { giftRegistryId: registry.id });
    };

    const handleCopyShareLink = (
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        giftRegistryId: number
    ) => {
        // safari restrictions: https://bugs.webkit.org/show_bug.cgi?id=222262
        clipboard.write([
            new ClipboardItem({
                "text/plain": new Promise((resolve) => {
                    lastValueFrom(
                        giftRegistryService.shareGiftRegistry(
                            channelName,
                            giftRegistryId
                        )
                    ).then(({ shareGiftRegistryUrl }) => {
                        resolve(
                            new Blob([shareGiftRegistryUrl], {
                                type: "text/plain",
                            })
                        );

                        isShareLinkCopied = true;
                        setTimeout(() => (isShareLinkCopied = false), 800);
                    });
                }),
            }),
        ]);
    };

    const handleOpenPopup = async (
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        giftRegistryId: number
    ) => {
        const { shareGiftRegistryUrl } = await lastValueFrom(
            giftRegistryService.shareGiftRegistry(channelName, giftRegistryId)
        );

        const popupManager = new PopupManager(shareGiftRegistryUrl);

        if (isMobile) {
            popupManager.open("_self");
        } else {
            popupManager.open();
        }
    };

    const handleOpenRegistryFormModal = async () => {
        await dynamicImportManager.fetch({
            definition: RegistryFormModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it
        setTimeout(() => {
            modalManager.openModal(registryFormModalToken, true, {
                actionType: REGISTRY_FORM_MODAL_ACTION_TYPES.update,
                registry,
            });
        }, 0);
    };
</script>

<div class="gw-gr-page-list">
    {#if !isSharedList}
        <div class="gw-gr-page-list__header">
            <div class="gw-gr-page-list__title-wrapper">
                <ButtonWithIcon on:click={handleToggleListOpen}>
                    <Icon
                        color="#777E90"
                        width={12}
                        height={12}
                        class={isListOpened
                            ? "gw-toggle-btn gw-toggle-btn-opened"
                            : "gw-toggle-btn gw-toggle-btn-closed"}
                    >
                        <ChevronIcon />
                    </Icon>
                </ButtonWithIcon>
                <BaseButton
                    class="gw-gr-page-list__header-title-btn"
                    on:click={handleToggleListOpen}
                >
                    {registry.title}
                </BaseButton>
                {#if isListOpened}
                    <ButtonWithIcon
                        class="gw-gr-page-list__header-action-btn"
                        on:click={handleOpenRegistryFormModal}
                    >
                        <Icon color="#777E90">
                            <EditIcon />
                        </Icon>
                    </ButtonWithIcon>
                {/if}
            </div>

            {#if isListOpened}
                <div
                    class={CN(
                        "gw-gr-page-list__header-line",
                        showConfirmDelete &&
                            "gw-gr-page-list__header-line_danger"
                    )}
                />

                <ButtonWithIcon
                    class="gw-gr-page-list__header-action-btn"
                    on:click={handleToggleConfirmDelete}
                >
                    <Icon color="#EF466F">
                        <DeleteIcon />
                    </Icon>
                </ButtonWithIcon>

                {#if registry.items.length && $enabledSocialShareChannels$?.length}
                    <WishlistSocialShare
                        on:share={handleShareRegistry}
                        label={$social_share_label$}
                        linkCopiedLabel={$link_copied_label$}
                        wishlistSocialShareChannels={$enabledSocialShareChannels$}
                        isShareLoading={$sharingStatus$ === STATUS.LOADING}
                        {isShareLinkCopied}
                    />
                {/if}
            {/if}
        </div>
    {/if}
    <div class="gw-gr-page-list__content" class:gw-opened={isListOpened}>
        <div class="gw-gr-page-list__details">
            <div class="gw-gr-page-list__event-name">
                <T {namespace} translationKey="event_name" />
                {" "}
                <span class="gw-gr-page-list__detail-value">
                    {registry.title}
                </span>
            </div>
            <div class="gw-gr-page-list__date">
                <T {namespace} translationKey="date" />
                {" "}
                <span class="gw-gr-page-list__detail-value">
                    {getHumanReadableDate(registry.date)}
                </span>
            </div>
            <div class="gw-gr-page-list__registrant-name">
                <T {namespace} translationKey="registrant_name" />
                {" "}
                <span class="gw-gr-page-list__detail-value">
                    {registry.registrant.firstName}
                    {" "}
                    {registry.registrant.lastName}
                </span>
            </div>
            {#if registry.registrant.coRegistrants?.length}
                <div class="gw-gr-page-list__co-registrant-name">
                    <T {namespace} translationKey="co_registrant_name" />
                    {" "}
                    <span class="gw-gr-page-list__detail-value">
                        {registry.registrant.coRegistrants[0].firstName}
                        {" "}
                        {registry.registrant.coRegistrants[0].lastName}
                    </span>
                </div>
            {/if}
        </div>
        {#if registry.items.length}
            <h4>
                <T {namespace} translationKey="products" />
            </h4>
            <div class="gw-gr-page-list__items-wrapper">
                <ul class="gw-gr-page-list__items">
                    {#each registry.items as item (new Set( [item.productId, item.variantId] ))}
                        <GiftRegistryPageItem
                            on:deleteItem
                            on:moveItem
                            on:updateQuantity
                            {item}
                            {registry}
                            {registries}
                            {productsQuery}
                            {giftCartService}
                            {drawerManager}
                            {dynamicImportManager}
                            isSharedItem={isSharedList}
                            {namespace}
                        />
                    {/each}
                </ul>
            </div>
        {:else}
            <div class="gw-gr-page-list__empty-state">
                <EmptyState title={$empty_products_label$}>
                    <svelte:fragment slot="icon">
                        <ProductEmptyIcon />
                    </svelte:fragment>
                    <svelte:fragment slot="footer">
                        {#if !isSharedList}
                            <a href={urlsBuilder.createCollectionAllUrl()}>
                                <T
                                    {namespace}
                                    translationKey="browse_products"
                                />
                            </a>
                        {/if}
                    </svelte:fragment>
                </EmptyState>
            </div>
        {/if}

        <div
            class={CN("gw-gr-page-list__delete-confirmation", {
                "gw-gr-page-list__delete-confirmation_visible":
                    showConfirmDelete,
            })}
        >
            <div class="gw-gr-page-list__delete-confirmation__title">
                <T {namespace} translationKey="delete_list_prompt_title" />
            </div>
            <div class="gw-gr-page-list__delete-confirmation__actions">
                <Button rounded on:click={handleDeleteRegistry}>
                    <T {namespace} translationKey="delete_list_prompt_accept" />
                    <slot name="icon">
                        <Icon color="#ffffff">
                            <DeleteIcon />
                        </Icon>
                    </slot>
                </Button>
                <Button
                    rounded
                    variant="outlined"
                    on:click={handleToggleConfirmDelete}
                >
                    <T {namespace} translationKey="delete_list_prompt_cancel" />
                </Button>
            </div>
        </div>
    </div>
</div>

<style lang="postcss">
    .gw-gr-page-list {
        &__header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            gap: 24px;
            margin-bottom: 24px;
            min-height: 38px;

            :global(&-title-btn) {
                font-size: inherit;
                font-weight: inherit;
                text-transform: inherit;
                display: flex;
                gap: 4px;
                align-items: center;
                white-space: nowrap;
            }

            &-line {
                height: 1px;
                flex: 1 0 auto;
                background: #e6e8ec;
                transition: all 0.3s;

                &_danger {
                    background: #ef466f;
                }
            }
        }

        &__title-wrapper {
            display: flex;
            gap: 4px;
            align-items: center;
            justify-content: flex-start;
            font-size: 14px;
            text-transform: uppercase;
            font-weight: 700;

            :global(.gw-toggle-btn) {
                transform: rotateX(0deg);
                transition: transform var(--gw-default-transition-duration);
            }

            :global(.gw-toggle-btn-opened) {
                transform: rotateX(-180deg);
            }
        }
        &__content {
            display: none;
            position: relative;
            &.gw-opened {
                display: block;
            }
        }

        &__details {
            margin-bottom: 24px;
            font-size: 14px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            line-height: normal;
        }

        &__detail-value {
            font-weight: 700;
        }

        &__items {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            padding: 0;

            @media all and (max-width: 480px) {
                grid-template-columns: repeat(2, 1fr);
            }
        }

        &__empty-state {
            a {
                color: var(--gw-gr-primary-link-color);
                font-weight: 600;
            }
        }

        &__delete-confirmation {
            padding: 16px 0;
            position: absolute;
            z-index: 10;
            top: 0;
            left: 0;
            width: 100%;
            background: #fff;
            box-shadow: 0 0 10px 10px #fff;
            transform: translateY(40px);
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s;
            &__title {
                margin-bottom: 16px;
                font-size: 14px;
                font-weight: bold;
                text-align: center;
            }
            &__actions {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                justify-content: center;

                :global(.gw-btn.--contained) {
                    background-color: #ef466f;
                    color: #fff;
                }

                :global(.gw-btn.--outlined) {
                    color: #000;
                    border: 2px solid #e6e8ec;
                    box-shadow: none;
                }
            }
            &_visible {
                visibility: visible;
                opacity: 1;
                transform: none;
            }
        }
    }
</style>
